/**
 * Central route constants for the entire Seatix application.
 * This is the single source of truth for all route definitions.
 *
 * When changing routes here, all navigation and routing configurations
 * throughout the application will automatically use the new values.
 */
export const ROUTES = {
  /**
   * Fan Portal Routes
   * Main customer-facing application routes
   */
  FAN_PORTAL: {
    ROOT: '',
    LOGIN: {
      ROOT: 'login',
    },
    FORGOT_PASSWORD: {
      ROOT: 'forgot-password',
    },
    RESET_PASSWORD: {
      ROOT: 'reset-password',
    },
    EVENTS: {
      ROOT: '', // Events list at root path '/'
      LIST: '', // Event list at root
      DETAILS: 'events/:eventSlug', // Event details: '/events/:eventSlug'
      SEAT_SELECTION: 'events/:eventSlug/seat-selection', // Seat selection: '/events/:eventSlug/seat-selection'
    },
    /**
     * A season is addressed by id, not slug: the API exposes no slug for one
     * (`GET /api/public/seasons/{id}`). Ids are stable, so the day a slug
     * exists these become 301 sources rather than a redirect chain.
     */
    SEASONS: {
      /**
       * The mount point the shell lazy-loads the season routes under. Season
       * routes hang off their own segment rather than a second empty path
       * alongside the event routes, so matching never depends on the router
       * backtracking out of one lazy branch into another.
       *
       * `ROOT` + `DETAILS_CHILD` must always compose to `DETAILS`; a spec pins it.
       */
      ROOT: 'seasons',
      DETAILS_CHILD: ':seasonId',
      DETAILS: 'seasons/:seasonId', // Season details: '/seasons/:seasonId'
      SEAT_SELECTION_CHILD: ':seasonId/seat-selection',
      SEAT_SELECTION: 'seasons/:seasonId/seat-selection', // '/seasons/:seasonId/seat-selection'
    },
    /**
     * A group is addressed by slug, not id: unlike a season it owns one, and
     * the API keys its detail endpoint on it (`GET /api/public/groups/{slug}`).
     * The slug is frozen after creation, so the address never moves.
     *
     * A group sells nothing of its own, so there is no seat-selection child
     * here — its dates are bought through their own event pages.
     *
     * `ROOT` + `DETAILS_CHILD` must always compose to `DETAILS`; a spec pins it.
     */
    GROUPS: {
      ROOT: 'groups',
      DETAILS_CHILD: ':groupSlug',
      DETAILS: 'groups/:groupSlug', // Group details: '/groups/:groupSlug'
    },
    PROFILE: {
      ROOT: 'profile',
      USER: 'user', // '/profile/user'
      TICKETS: 'tickets', // '/profile/tickets'
      TICKET_DETAILS: 'tickets/:eventId', // '/profile/tickets/:eventId'
    },
    LEGAL: {
      ROOT: 'legal',
      TERMS: 'terms', // '/legal/terms'
      PRIVACY: 'privacy', // '/legal/privacy'
    },
    HELP: {
      FAQ: 'help/faq', // '/help/faq'
      POS: 'help/sales-centers', // '/help/sales-centers'
    },
  },

  /**
   * Checkout Routes
   * Separate checkout flow routes
   */
  CHECKOUT: {
    ROOT: 'checkout',
    ORDER: ':orderToken', // '/checkout/:orderToken'
    SUCCESS: ':orderToken/success', // '/checkout/:orderToken/success'
  },

  /**
   * Control Hub Landing Routes
   * Public-facing marketing site for the control hub product
   */
  CONTROL_HUB_LANDING: {
    ROOT: '',
    SOLICITAR_INFORMACION: 'solicitar-informacion',
    SECTIONS: {
      INICIO: 'inicio',
      CONTROL: 'control',
      POR_QUE: 'por-que',
      OTRO_NIVEL: 'otro-nivel',
      CATEGORIAS: 'categorias',
    },
  },

  /**
   * Control Hub Routes (Admin)
   * Back-office application routes
   */
  CONTROL_HUB: {
    DASHBOARD: '', // Control Hub root
    AUTHORIZATION: 'authorization',
    BOX_OFFICE: {
      ROOT: 'box-office',
      LAST_SALES: 'last', // '/box-office/last'
      EVENT_DETAILS: ':id', // '/box-office/:id'
    },
    EVENT_CREATION: {
      ROOT: 'event-creation',
      CREATION_STEPS: 'creation-steps',
      CREATION_ID: 'creation/:id',
    },
    SEATS_MANAGEMENT: {
      ROOT: 'seats-management',
      EVENT_DETAILS: ':id', // '/seats-management/:id'
    },
    STATISTICS: 'statistics',
    EARNINGS: 'earnings',
    REPORTS: 'reports',
    SETTINGS: {
      ROOT: 'settings',
      TICKETS: 'tickets',
      USER: 'user',
    },
  },
} as const;

/**
 * Route parameter type definitions for type safety
 */
export interface RouteParams {
  eventSlug: string;
  eventId: string;
  seasonId: string;
  groupSlug: string;
  orderToken: string;
  id: string;
}

/**
 * Specific route parameter interfaces
 */
export type EventRouteParams = Pick<RouteParams, 'eventSlug'>;
export type CheckoutRouteParams = Pick<RouteParams, 'orderToken'>;
export type EventIdRouteParams = Pick<RouteParams, 'eventId'>;
export type SeasonRouteParams = Pick<RouteParams, 'seasonId'>;
export type GroupRouteParams = Pick<RouteParams, 'groupSlug'>;
export type GenericIdRouteParams = Pick<RouteParams, 'id'>;
